/**
 * Composition step for automated-traffic suppression: classify the visitor once, and for a bot opt
 * every configured GA measurement out at the tag.
 *
 * Tag-level opt-out is needed in addition to the `AnalyticsService` bot gate because gtag is reached
 * outside registered providers — the nuxt-gtag module injects and configures the primary tag, the
 * client plugin writes GA4 User-ID directly, and GoKollab configures a second measurement ID for
 * logged-in CP users. `gtag('config', id)` sends an automatic page view, so a stream that is
 * configured but not opted out records the visit even when no provider ever dispatches an event.
 * Every measurement ID the app can configure therefore has to be listed here.
 */

import { disableGoogleAnalyticsMeasurements } from './disable-google-analytics.utils'
import { isAnalyticsBotTraffic, readAnalyticsBotSignals } from './is-analytics-bot.utils'
import type { AnalyticsBotSignals } from './is-analytics-bot.utils'
import type { GoogleAnalyticsRegistrationConfig } from './register-google-analytics-provider.utils'

interface AnalyticsBotSuppressionLogger {
  info: (message: string, context?: Record<string, unknown>) => void
}

export interface AnalyticsBotSuppressionOptions {
  /** Same shape provider registration reads, so the primary measurement ID has one source. */
  analyticsConfig: GoogleAnalyticsRegistrationConfig
  /** Streams configured on the tag outside provider registration (GoKollab's CP stream). */
  secondaryMeasurementIds?: readonly (string | undefined | null)[]
  /** Object carrying the `ga-disable-<id>` flags — `window` in the client plugin. */
  target: Record<string, unknown> | undefined
  /** Injectable for tests; defaults to the live browser signals. */
  signals?: AnalyticsBotSignals
  logger?: AnalyticsBotSuppressionLogger
}

/** Returns whether this visit is automated traffic, after applying tag-level opt-out for one. */
export function applyAnalyticsBotSuppression({
  analyticsConfig,
  secondaryMeasurementIds = [],
  target,
  signals,
  logger,
}: AnalyticsBotSuppressionOptions): boolean {
  const isBotTraffic = isAnalyticsBotTraffic(signals ?? readAnalyticsBotSignals())
  if (!isBotTraffic) return false

  const measurementIds = [analyticsConfig.providers?.googleAnalytics?.measurementId, ...secondaryMeasurementIds]
  const disabledCount = disableGoogleAnalyticsMeasurements(measurementIds, target)
  logger?.info('[Analytics] Automated traffic detected — provider delivery suppressed', {
    disabledMeasurements: disabledCount,
  })

  return true
}
